// Runtime content loader — fetches editable JSON from /content/*.json (served
// as-is from Hostinger, so a File Manager edit takes effect without a rebuild).
// Falls back to the frozen snapshot below (baked into the JS bundle) if the
// live file is missing or fails validation, so a bad manual edit degrades
// gracefully instead of crashing the site.
import useSWR from "swr";
import { z } from "zod";
import destinationsFallback from "./content-fallback/destinations.json";
import testimonialsFallback from "./content-fallback/testimonials.json";
import faqsFallback from "./content-fallback/faqs.json";

const faqItemSchema = z.object({
  q: z.string(),
  a: z.string(),
});

const packageSchema = z.object({
  id: z.string(),
  name: z.string(),
  duration: z.string(),
  startingFrom: z.number(),
  highlights: z.array(z.string()),
  inclusions: z.array(z.string()),
  exclusions: z.array(z.string()),
});

const bestTimeSchema = z.object({
  months: z.string(),
  weather: z.string(),
  crowd: z.string(),
  recommended: z.string(),
});

const experienceSchema = z.object({
  name: z.string(),
  image: z.string(),
  alt: z.string(),
});

const destinationSchema = z.object({
  slug: z.string(),
  path: z.string(),
  name: z.string(),
  shortName: z.string(),
  hook: z.string(),
  startingFrom: z.number(),
  heroImage: z.string(),
  heroAlt: z.string(),
  cardImage: z.string(),
  cardAlt: z.string(),
  meta: z.object({
    title: z.string(),
    description: z.string(),
    keywords: z.string(),
  }),
  intro: z.string(),
  packages: z.array(packageSchema),
  bestTime: z.array(bestTimeSchema),
  experiences: z.array(experienceSchema),
  faqs: z.array(faqItemSchema),
});

const testimonialSchema = z.object({
  name: z.string(),
  location: z.string(),
  quote: z.string(),
  rating: z.number(),
});

const faqCategorySchema = z.object({
  id: z.string(),
  name: z.string(),
  items: z.array(faqItemSchema),
});

const destinationsSchema = z.array(destinationSchema);
const testimonialsSchema = z.array(testimonialSchema);
const faqCategoriesSchema = z.array(faqCategorySchema);

const fetcher = (url) =>
  fetch(url).then((res) => {
    if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);
    return res.json();
  });

const useContent = (key, schema, fallback) => {
  const { data, error, isLoading } = useSWR(`/content/${key}.json`, fetcher, {
    revalidateOnFocus: false,
  });

  if (isLoading) return { data: undefined, isLoading: true };

  if (error || !data) {
    console.warn(`[content] Could not load /content/${key}.json — using last-known-good content.`, error);
    return { data: fallback, isLoading: false };
  }

  const parsed = schema.safeParse(data);
  if (!parsed.success) {
    console.warn(`[content] /content/${key}.json failed validation — using last-known-good content.`, parsed.error.flatten());
    return { data: fallback, isLoading: false };
  }

  return { data: parsed.data, isLoading: false };
};

export const useDestinations = () => useContent("destinations", destinationsSchema, destinationsFallback);
export const useTestimonials = () => useContent("testimonials", testimonialsSchema, testimonialsFallback);
export const useFaqCategories = () => useContent("faqs", faqCategoriesSchema, faqsFallback);

export const getDestinationBySlug = (destinations, slug) => (destinations || []).find((d) => d.slug === slug);
export const flatFaqs = (categories) => (categories || []).flatMap((c) => c.items);
